import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useDispatchAsync } from '@src/utils/dispatch'
import { useSnack } from '@src/utils/navigatorContext'
import {
  AddressCountry,
  BillingProduct,
  LegalPolicyConsentKind,
  LegalPolicyConsentState,
  Product,
  ProductGroup,
  ProductMin,
  PromotionCode,
  Subscription,
  SubscriptionCheckoutOptionKind,
  SurveysConfigKind,
} from '@src/types'
import { productsSelector, subscriptionsSelector, userSelector } from '@src/selectors/app'
import { promoCodeSelector, subscriptionSchedulesSelector } from '@src/selectors/marketplace'
import { useActiveSubscriptions } from '@src/hooks/useActiveSubscriptions'
import { RootStoreState } from '@src/models/app.types'
import {
  AppStackParamList,
  CommonAuthorizedStackParamList,
  OnboardingStackParamsList,
} from '@src/navigation/types'
import { UpgradeSubscriptionTrialMutation } from '@src/screens/Marketplace/graphql/upgradeSubscriptionTrial.generated'
import { EstimateTaxQuery } from '@src/screens/Marketplace/graphql/estimateTax.generated'
import { Feature, useFeatureFlag } from '@src/components'
import { loadingEffectsSelector } from '@src/selectors/loading'
import { policyConsentStatusesSelector } from '@src/selectors/policyConsents'
import { CheckoutKind, GroupableProduct, isGroupedProducts, isProduct } from '../types/types'
import { UpgradeSubscriptionProductMutation } from '../graphql/upgradeSubscriptionProduct.generated'
import { PromotionCodeQuery } from '../graphql/activateDiscount.generated'
import { trackSubscription } from './trackSubscription'

export { usePurchaseSubscription } from './hooks/usePurchaseSubscription'
export { usePurchaseOneTimePayment } from './hooks/usePurchaseOneTimePayment'
export { useSelectedAddonModal } from './hooks/useSelectedAddonModal'

const getEligibleAddons = <T extends Pick<Product, 'core' | 'id'>>(
  products: Array<Pick<Product, 'id'> & { incompatibleAddons: { id: string }[] }>,
  upgradeProducts: Array<Pick<Product, 'id'>>,
  allProducts: T[],
) => {
  const productIds = products.map((product) => product.id)
  const incompatibleAddonIds = products.flatMap((product) =>
    product.incompatibleAddons.map((addon) => addon.id),
  )

  const upgradeProductIds = upgradeProducts.map((product) => product.id)

  return allProducts.filter(
    (product) =>
      !product.core &&
      !incompatibleAddonIds.includes(product.id) &&
      !upgradeProductIds.includes(product.id) &&
      !productIds.includes(product.id),
  )
}

export const useSubscriptionFromRoute = () => {
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const { params } = useRoute<
    RouteProp<
      AppStackParamList,
      | 'CancelSubscriptionConfirmation'
      | 'CancelSubscriptionOnCommitment'
      | 'ChangeBillingCycleModal'
      | 'ChangeBillingCycleSuccessModal'
      | 'CancelSubscriptionOtherOptions'
      | 'ChurnRecoveryMembershipOffer'
      | 'ChurnRecoveryOptions'
      | 'ChurnRecoveryUpsellOffer'
      | 'ChurnRecoveryUpsellOfferConfirmation'
      | 'PauseSubscription'
      | 'PauseSubscriptionReview'
      | 'ProgramDetails'
      | 'ReactivateSubscriptionModal'
    >
  >()
  const { subscriptionId } = params

  const subscription = useSelector((state: RootStoreState) =>
    subscriptionsSelector(state).find((subscription) => subscription.id === subscriptionId),
  )

  useEffect(() => {
    if (!subscription) {
      navigation.replace('Subscriptions')
    }
  }, [navigation, subscription])

  return { subscription }
}

export const useActiveSubscriptionAddons = (
  subscription?: Pick<Subscription, 'currentPeriodEndAt' | 'commitmentEndAt'> & {
    primaryProduct: { id: string }
  },
) => {
  const allSubscriptions = useSelector(subscriptionsSelector)
  const allProducts = useSelector(productsSelector)

  if (!subscription) {
    return []
  }

  let currentSubscriptionWillBeCanceledAt = moment(subscription.currentPeriodEndAt)

  if (subscription.commitmentEndAt) {
    const commitmentEndsAt = moment(subscription.commitmentEndAt)
    const commitmentDaysLeft = commitmentEndsAt.diff(moment(), 'days')

    if (commitmentDaysLeft > 0) {
      currentSubscriptionWillBeCanceledAt = commitmentEndsAt
    }
  }

  const allActiveSubscriptions = allSubscriptions.filter(
    (subscription) =>
      subscription.status === 'active' &&
      (!subscription.cancelAt ||
        moment(subscription.cancelAt).isAfter(currentSubscriptionWillBeCanceledAt)),
  )

  // subscription.primaryProduct is a ProductMin without the 'core' property which we need for filtering
  const allActiveSubscriptionProducts = allActiveSubscriptions
    .map((subscription) => subscription.primaryProduct.id)
    .map((id) => allProducts.find((product) => product.id === id))
    .filter((product): product is Product => !!product)

  const activeAddonProductIds = allActiveSubscriptionProducts
    .filter((product) => !product.core && !(subscription.primaryProduct.id === product.id))
    .map((product) => product.id)

  return allActiveSubscriptions
    .map((subscription) => subscription.primaryProduct)
    .filter((product) => activeAddonProductIds.includes(product.id))
}

export const useActivateDiscount = () => {
  const dispatch = useDispatch()
  const isSubmitting = useRef(false)

  return useCallback(
    ({
      code,
      product,
      success,
      failure,
    }: {
      code: string
      product?: string
      success?: ({ promotionCode }: PromotionCodeQuery) => void
      failure?: (error: any) => void
    }) => {
      if (isSubmitting.current) {
        return
      }

      isSubmitting.current = true

      dispatch({
        type: 'marketplace/activateDiscount',
        payload: { code, product },
        success,
        failure,
        complete: () => {
          isSubmitting.current = false
        },
      })
    },
    [dispatch],
  )
}

export const useProducts = () => {
  const activeSubscriptions = useActiveSubscriptions()
  const schedules = useSelector(subscriptionSchedulesSelector)
  const products = useSelector(productsSelector)
  const activeSubscriptionProducts = activeSubscriptions.map(
    (subscription) => subscription.primaryProduct,
  )
  const activeSubscriptionProductIds = activeSubscriptionProducts.map((product) => product.id)

  const activeSubscriptionUpgradeProducts = activeSubscriptions.flatMap(
    (subscription) => subscription.primaryProduct.eligibleUpgradeProducts,
  )

  const scheduledPhases = schedules.flatMap((schedule) => schedule.phases)
  const upcomingSubscriptionPhases = scheduledPhases.filter((phase) => {
    const phaseProduct = phase.primaryProduct

    return (
      moment(phase.startAt).isAfter(moment()) &&
      phaseProduct &&
      !activeSubscriptionProductIds.includes(phaseProduct.id)
    )
  })

  const eligibleAddons = getEligibleAddons(
    activeSubscriptionProducts,
    activeSubscriptionUpgradeProducts,
    products,
  )

  const dietitianAddons = eligibleAddons.filter(
    (product) => product.dietitian || product.dietitianMenu,
  )
  const oneTimeAddons = eligibleAddons.filter((addon) => !addon.dietitian && !addon.dietitianMenu)

  return {
    activeSubscriptions,
    products,
    upcomingSubscriptionPhases,
    dietitianAddons,
    oneTimeAddons,
  }
}

export const useEstimateTax = () => {
  const dispatchAsync = useDispatchAsync()

  const estimateTax = useCallback(
    async ({
      zipCode,
      country,
      productKey,
      providerPromotionCodeId,
    }: {
      zipCode: string
      country: string
      productKey: BillingProduct
      providerPromotionCodeId?: string | null
    }) => {
      const {
        estimateTax: { estimatedTax },
      } = await dispatchAsync<EstimateTaxQuery>({
        type: 'marketplace/estimateTax',
        payload: {
          product: productKey,
          providerPromotionCodeId,
          zipCode,
          country,
        },
      })
      return estimatedTax
    },
    [dispatchAsync],
  )

  return estimateTax
}

export const useTaxEstimation = ({
  productKey,
  promotionCode,
  zipCode,
  country,
}: {
  productKey?: BillingProduct
  promotionCode?: PromotionCode | null
  zipCode?: string
  country?: string | null
}) => {
  const showSnack = useSnack()

  const [originalEstimatedTax, setEstimatedTax] = useState(0)
  const loadingEffects = useSelector(loadingEffectsSelector)
  const loading = loadingEffects['marketplace/estimateTax'] || false

  const estimatedTax = zipCode && productKey ? originalEstimatedTax : 0

  const providerPromotionCodeId = promotionCode?.providerId ?? null

  const estimateTax = useEstimateTax()

  useEffect(() => {
    if (!zipCode || !productKey) {
      return
    }

    estimateTax({
      zipCode,
      country: country || AddressCountry.Us,
      productKey,
      providerPromotionCodeId,
    })
      .then(setEstimatedTax)
      .catch(() => {
        showSnack("Couldn't estimate tax!", null, 'error')
        setEstimatedTax(0)
      })
  }, [country, estimateTax, productKey, providerPromotionCodeId, showSnack, zipCode])

  return { estimatedTax, loading }
}

export const useUpgradeSubscriptionTrial = () => {
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation()
  const user = useSelector(userSelector)
  const showSnack = useSnack()

  const paymentMethod = user?.paymentMethod

  return async (subscriptionId: string, promotionCode?: PromotionCode | null) => {
    if (!subscriptionId || !paymentMethod) {
      return
    }

    const upgradeResponse = await dispatchAsync<UpgradeSubscriptionTrialMutation>({
      type: 'marketplace/upgradeSubscriptionTrial',
      payload: {
        id: subscriptionId,
        providerPaymentMethodId: paymentMethod.stripeId,
        providerPromotionCodeId: promotionCode?.providerId ?? null,
      },
    })
    const { subscription } = upgradeResponse.upgradeSubscriptionTrial

    const onSubscriptionUpgradeSuccess = async () => {
      trackSubscription({
        subscriptionTotal: subscription.price,
        subscriptionId: subscription.id,
        onboarding: false,
        primaryProductId: subscription.primaryProduct.id,
        nickname: subscription.primaryProduct.title,
      })

      await dispatchAsync({ type: 'users/fetch' })

      navigation.navigate('Subscriptions')
    }

    // complimentary subscriptions have a set date for cancelAt
    // thus we can assert the subscription was updated if cancelAt is null
    if (!subscription.cancelAt) {
      await onSubscriptionUpgradeSuccess()
      return
    }

    showSnack("Couldn't complete transaction!", null, 'error')
  }
}

export const useUpgradeSubscriptionProduct = () => {
  const showSnack = useSnack()
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation<
    StackNavigationProp<
      AppStackParamList & OnboardingStackParamsList & CommonAuthorizedStackParamList
    >
  >()
  const user = useSelector(userSelector)

  const paymentMethod = user?.paymentMethod

  return useCallback(
    async (
      subscriptionId: string,
      product: Pick<Product, 'id' | 'key' | 'trialDays'>,
      checkoutOptionKinds?: SubscriptionCheckoutOptionKind[],
      promotionCode?: PromotionCode | null,
    ) => {
      if (!subscriptionId) {
        return
      }

      const upgradeResponse = await dispatchAsync<UpgradeSubscriptionProductMutation>({
        type: 'marketplace/upgradeSubscriptionProduct',
        payload: {
          id: subscriptionId,
          nextProduct: product.key,
          providerPaymentMethodId: paymentMethod?.stripeId,
          checkoutOptionKinds,
          providerPromotionCodeId: promotionCode?.providerId ?? null,
        },
      })
      const { subscription } = upgradeResponse.upgradeSubscriptionProduct

      const onSubscriptionUpgradeSuccess = async () => {
        trackSubscription({
          subscriptionTotal: subscription.price,
          subscriptionId: subscription.id,
          onboarding: false,
          primaryProductId: subscription.primaryProduct.id,
          trial: !!product.trialDays && product.trialDays > 0,
          nickname: subscription.primaryProduct.title,
        })

        if (checkoutOptionKinds?.includes(SubscriptionCheckoutOptionKind.Stelo)) {
          navigation.reset({
            index: 0,
            routes: [
              {
                name: 'SteloPaymentSuccessful',
              },
            ],
          })
          await dispatchAsync({ type: 'users/fetch' })
        } else {
          await dispatchAsync({ type: 'users/fetch' })
          navigation.navigate('Subscriptions')
        }
      }

      if (subscription.id) {
        await onSubscriptionUpgradeSuccess()
        return
      }

      showSnack("Couldn't complete transaction!", null, 'error')
      navigation.navigate('Subscriptions')
    },
    [dispatchAsync, navigation, paymentMethod?.stripeId, showSnack],
  )
}

export const usePromotionCode = (product?: BillingProduct, promoCode?: PromotionCode | null) => {
  const storedPromoCode = useSelector(promoCodeSelector)
  const activateDiscount = useActivateDiscount()

  // apply promo code from params only once
  const promoCodeAppliedRef = useRef(false)

  const promotionCode = promoCodeAppliedRef.current ? storedPromoCode : promoCode || storedPromoCode

  useEffect(() => {
    // Makes sure promotion code is applicable to current product
    if (promotionCode?.code && product) {
      promoCodeAppliedRef.current = true
      activateDiscount({
        code: promotionCode.code,
        product,
      })
    }
  }, [promotionCode?.code, product, activateDiscount])

  return promotionCode
}

const byOrder = (a: GroupableProduct, b: GroupableProduct) => a.order - b.order

export const useGroupedProducts = (products: Product[]): Array<GroupableProduct> =>
  useMemo(() => {
    const groups = Object.values(
      products.reduce((acc: Record<string, ProductGroup>, product) => {
        if (product.productGroup) {
          acc[product.productGroup.id] = product.productGroup
        }
        return acc
      }, {}),
    )

    const groupableProducts = groups.map(
      (group) =>
        ({
          ...group,
          products: products
            .filter((product) => product.productGroup?.id === group.id)
            .sort(byOrder),
        } as GroupableProduct),
    )

    const productsWithoutGroups = products.filter((product) => !product.productGroup?.id)

    return groupableProducts
      .concat(productsWithoutGroups)
      .filter((item) => isProduct(item) || item.products.length > 0)
      .sort(byOrder)
  }, [products])

export const useInsuranceCoverage = () => {
  const insuranceCoveredVideoConsultFeature = useFeatureFlag(Feature.InsuranceCoveredVideoConsult)

  const user = useSelector(userSelector)

  const userEligibleForInsuranceCoverage =
    user && 'eligibleForInsuranceCoverage' in user ? user.eligibleForInsuranceCoverage : false

  return useCallback(
    (item: Product) => {
      return (
        insuranceCoveredVideoConsultFeature &&
        item.insuranceCoverage &&
        userEligibleForInsuranceCoverage
      )
    },
    [insuranceCoveredVideoConsultFeature, userEligibleForInsuranceCoverage],
  )
}

export const useByosProducts = () => {
  const dispatch = useDispatch()
  const products = useSelector(productsSelector)
  const byosProducts = products.filter((product) => product.ownSensor)

  useEffect(() => {
    dispatch({ type: 'app/fetchProducts' })
  }, [dispatch])

  return byosProducts
}

export const useSubscriptionEligibleForByosUpgrade = () => {
  const subscriptions = useActiveSubscriptions()

  return subscriptions.find((subscription) =>
    subscription.primaryProduct.eligibleUpgradeProducts.some((product) => product.ownSensor),
  )
}

export const useCanUpgradeToProduct = (
  subscription: Subscription | undefined,
  productId: string | undefined,
) => {
  if (!subscription || !productId) {
    return false
  }
  const eligibleUpgradeProducts = subscription.primaryProduct.eligibleUpgradeProducts
  return eligibleUpgradeProducts.some((product) => product.id === productId)
}

export const useNavigateToSteloCheckout = () => {
  const user = useSelector(userSelector)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const policyConsentStatuses = useSelector(policyConsentStatusesSelector)

  const navigateToSteloCheckout = (
    checkoutKind?: CheckoutKind,
    subscriptionId?: string,
    product?: Product,
  ) => {
    const medicalDisclaimerSurveyFinished = user?.surveyLinks.find(
      (link) => link.survey.kind === SurveysConfigKind.CgmMedicalDisclaimer,
    )?.finished

    const medicalDisclaimerConsentAccepted = policyConsentStatuses.some(
      (status) =>
        status.kind === LegalPolicyConsentKind.MedicalDisclaimer &&
        status.lastPolicyConsent?.state === LegalPolicyConsentState.Accepted,
    )

    if (medicalDisclaimerSurveyFinished || medicalDisclaimerConsentAccepted) {
      navigation.replace('SteloCheckout', { checkoutKind, subscriptionId, product })
    } else {
      navigation.replace('Questionnaire', {
        questionnaire: SurveysConfigKind.CgmMedicalDisclaimer,
        allowEarlyExit: true,
        nextScreen: { screen: 'SteloCheckout', params: { checkoutKind, subscriptionId, product } },
      })
    }
  }

  return { navigateToSteloCheckout }
}

export const useNavigateToByosCheckout = () => {
  const byosProducts = useByosProducts()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()

  const navigateToByosCheckout = () => {
    navigation.replace('EligibilityCheck', {
      product: byosProducts[0],
      nextScreen: {
        screen: 'OwnSensorSelection',
        params: {
          nextScreen: 'FreeTrialSubscriptionOffer',
          params: {
            product: byosProducts[0],
          },
        },
      },
    })
  }

  return { navigateToByosCheckout }
}

export const useNavigateToByosUpgrade = () => {
  const byosProducts = useByosProducts()
  const subscriptionEligibleForByosUpgrade = useSubscriptionEligibleForByosUpgrade()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()

  const upgradableByosProducts = byosProducts.filter((byosProduct) =>
    subscriptionEligibleForByosUpgrade?.primaryProduct.eligibleUpgradeProducts.some(
      (product) => product.id === byosProduct.id,
    ),
  )
  const upgradableByosProduct = upgradableByosProducts[0]

  const questionnaireNextScreen =
    upgradableByosProducts.length > 0 && upgradableByosProduct.price === 0
      ? {
          screen: 'PurchaseFreeByosProduct',
          params: {
            product: upgradableByosProduct,
            subscription: subscriptionEligibleForByosUpgrade,
          },
        }
      : {
          screen: 'FreeTrialSubscriptionOffer',
          params: {
            product: upgradableByosProduct,
            subscription: subscriptionEligibleForByosUpgrade,
          },
        }

  const navigateToByosUpgrade = (replace = false) => {
    const params = {
      product: upgradableByosProduct,
      nextScreen: {
        screen: 'Questionnaire',
        params: {
          questionnaire: SurveysConfigKind.OwnSensor,
          nextScreen: questionnaireNextScreen,
        },
      },
    } as const

    if (replace) {
      navigation.replace('EligibilityCheck', params)
    } else {
      navigation.navigate('EligibilityCheck', params)
    }
  }

  return {
    upgradableByosProducts,
    subscriptionEligibleForByosUpgrade,
    navigateToByosUpgrade,
  }
}

export const useEligibleCgmUpgradeProducts = (
  products: Product[],
  eligibleUpgradeProducts: Pick<ProductMin, 'key'>[],
) => {
  const groupedProducts = useGroupedProducts(products)
  const cgmProductGroup = groupedProducts.find((groupedProduct) =>
    isGroupedProducts(groupedProduct),
  )

  if (!cgmProductGroup) {
    return []
  }

  return cgmProductGroup.products.filter((product) =>
    eligibleUpgradeProducts.find((p) => p.key === product.key),
  )
}
